import { FluentBundle, FluentResource } from "@fluent/bundle"
import { negotiateLanguages } from "@fluent/langneg"
import { Logger } from "../../Helpers/logging"

export type LanguageTranslationMap = Record<string, string>
export type Translations = Record<string, LanguageTranslationMap>

const RE_ENUM_REPLACE = /_/g
const RE_VALID_KEY = /^(-?[a-zA-Z][\w-]*)$/

const logger = new Logger("localization")

const isValidKey = (key: string): boolean => RE_VALID_KEY.test(key)

// We take the json object from translations and converts it to the fluent format FTL.
export function buildFtl(locale: string, messages: LanguageTranslationMap | undefined | null): string {
    let ftl = ""

    if (messages) {
        for (const [key, value] of Object.entries(messages)) {
            ftl += `${key} = ${value}\n\n`

            if (!isValidKey(key)) {
                // tests for syntax errors
                // Fluent doesn't seem to report syntax errors in any way
                logger.warn(`Invalid key in translation for ${locale}: '${key}' [will use fallback text]`)
            }
        }
    }

    return ftl
}

// takes the supportd locals or saved locales and finds the best match. navigator.language is locales from the browser
export function pickBestLocales(supportedLocales: string[], defaultLocale: string, savedUserLocale?: string): string[] {
    const userLocales = savedUserLocale ? [savedUserLocale, ...navigator.languages] : navigator.languages

    logger.info(
        `Picking best match between user locales and supported locales [user locales: [${userLocales.join(", ")}], supported locales: [${supportedLocales.join(", ")}]]`,
        userLocales,
        supportedLocales
    )

    // There will only ever be 1 selected local either default or suported or default locale
    const selectedLocales = negotiateLanguages(userLocales, supportedLocales, {
        strategy: "lookup",
        defaultLocale: defaultLocale,
    })
    logger.info(`Selected locales: [${selectedLocales.join(", ")}]`, selectedLocales)

    return selectedLocales
}

export function* generateBundles(selectedLocales: string[], translations: Translations): Iterable<FluentBundle> {
    logger.info(
        `Generating bundles for locales and translations [selected locales: [${selectedLocales.join(", ")}], translations: [${Object.keys(translations).join(", ")}]]`,
        selectedLocales,
        translations
    )

    for (const locale of selectedLocales) {
        const messages = translations[locale]
        const len = messages ? Object.keys(messages).length : 0

        logger.info(`Building bundle for ${locale} with ${len} messages`)

        const ftl = buildFtl(locale, messages)
        const resource = new FluentResource(ftl)
        const bundle = new FluentBundle(locale)
        bundle.addResource(resource)

        yield bundle
    }
}

export function getTranslationIdForEnum(base: string, enumValue: string) {
    const transformedEnumVal = enumValue.toLowerCase().replace(RE_ENUM_REPLACE, "-")
    const id = `${base}-${transformedEnumVal}`
    return id
}
